import React, { useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import { mdiCameraOutline } from "@mdi/js";
import { toast } from "react-toastify";
import webApiClient from "../../api/client";
import "./InstructorAvatarUpload.css";

/**
 * InstructorAvatarUpload — circular avatar editor for instructor profile photo.
 * Compresses on file select and shows a preview. User confirms with Save or
 * discards with Cancel. Spinner during save, toast success/error.
 */
const InstructorAvatarUpload = ({
    currentImage,
    organisationId,
    onSaveSuccess,
    onSaveError,
    onPreviewChange,
    externalPendingBase64,
    onExternalCancel,
}) => {
    const inputRef = useRef(null);
    const [previewBase64, setPreviewBase64] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // A newly chosen preset supersedes a pending file preview (the mirror of
    // handleFileChange cancelling the preset), so display and Save always agree.
    useEffect(() => {
        if (externalPendingBase64) setPreviewBase64(null);
    }, [externalPendingBase64]);

    const compressImage = (file) => {
        return new Promise((resolve) => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const img = new Image();

            img.onload = () => {
                let { width, height } = img;
                const maxWidth = 800;
                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }
                canvas.width = width;
                canvas.height = height;
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL("image/jpeg", 0.2));
            };

            img.src = URL.createObjectURL(file);
        });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = "";

        const base64 = await compressImage(file);
        // TODO: open crop modal here before showing preview
        // A manually chosen file supersedes a pending preset avatar
        if (onExternalCancel) onExternalCancel();
        setPreviewBase64(base64);
        if (onPreviewChange) onPreviewChange(base64);
    };

    const handleSave = async () => {
        // Save whichever image is pending; an external preset takes priority over a chosen file
        const raw = externalPendingBase64 || previewBase64;
        if (!raw) return;
        setIsSaving(true);
        // Strip data URL prefix — backend expects pure base64, not "data:image/jpeg;base64,..."
        const pureBase64 = raw.includes(',') ? raw.split(',')[1] : raw;
        const response = await webApiClient.patch("/api/v2/instructor/manage/profile/photo", {
            base64Image: pureBase64,
        });
        setIsSaving(false);
        if (response.ok) {
            toast.success("Profile photo saved.");
            setPreviewBase64(null);
            if (onExternalCancel) onExternalCancel();
            if (onSaveSuccess) onSaveSuccess(raw, response.data);
        } else {
            const msg = response.data?.message || "Failed to save photo. Please try again.";
            toast.error(msg);
            if (onSaveError) onSaveError(response);
        }
    };

    const handleCancel = () => {
        setPreviewBase64(null);
        if (onPreviewChange) onPreviewChange(null);
        if (onExternalCancel) onExternalCancel();
    };

    const displayImage = previewBase64 || externalPendingBase64 || currentImage;
    const isPreviewing = !!previewBase64 || !!externalPendingBase64;

    return (
        <div className="instructor-avatar-upload">
            <button
                type="button"
                className={`avatar-btn${isPreviewing ? " is-previewing" : ""}`}
                onClick={() => !isSaving && inputRef.current?.click()}
                aria-label="Change profile photo"
                disabled={isSaving}
            >
                {displayImage ? (
                    <img src={displayImage} alt="Profile" className="avatar-img" />
                ) : (
                    <div className="avatar-placeholder">
                        <Icon path={mdiCameraOutline} size={1.5} color="var(--dl-color-purple-default)" />
                    </div>
                )}

                {displayImage && !isPreviewing && (
                    <div className="avatar-overlay">
                        <Icon path={mdiCameraOutline} size={1.2} color="#ffffff" />
                    </div>
                )}

                {isSaving && <div className="avatar-spinner" />}
            </button>

            {isPreviewing ? (
                <div className="avatar-preview-actions">
                    <button
                        type="button"
                        className="avatar-save-btn"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving…" : "Save"}
                    </button>
                    <button
                        type="button"
                        className="avatar-cancel-btn"
                        onClick={handleCancel}
                        disabled={isSaving}
                    >
                        Cancel
                    </button>
                </div>
            ) : (
                <span className="avatar-hint">Click to change photo</span>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
        </div>
    );
};

export default InstructorAvatarUpload;
